/**
 * Credential Management Composable
 * Credential loading, selection, filtering by type (mycoid2016 / idpro)
 */
import { ref, computed } from 'vue';
import { credentialApi } from '@/features/Entity_my/api/credentialApi';
import type { Credential } from '@/features/Entity_my/types/credential';

export type CredentialFilterType = 'mycoid2016' | 'idpro';

export interface CredentialManagementOptions {
  credentialType?: CredentialFilterType;
}

export function useCredentialManagement(options?: CredentialManagementOptions) {
  const credentialType = options?.credentialType || 'mycoid2016';

  const credentials = ref<Credential[]>([]);
  const isLoadingCredentials = ref(false);
  const selectedCredentialId = ref<string | null>(null);
  const showManageCredentialDialog = ref(false);

  const selectedCredential = computed(() =>
    credentials.value.find(c => c.id === selectedCredentialId.value) || null
  );

  const isMatchingCredential = (credential: Credential): boolean => {
    const alias = credential.alias?.toLowerCase() || '';
    if (credentialType === 'idpro') {
      return (credential as any).registryType === 'B48002' || alias.includes('idpro') || alias.includes('ssm4u');
    }
    return alias.includes('mycoid') || alias.includes('mycoid2016');
  };

  /** Display label for credential type */
  const credentialTypeLabel = computed(() =>
    credentialType === 'idpro' ? 'IDPRO (SSM4U)' : 'MYCOID2016'
  );

  const mycoidCredentials = computed(() =>
    credentials.value.filter(isMatchingCredential)
  );

  const loadCredentials = async () => {
    isLoadingCredentials.value = true;
    try {
      const response = await credentialApi.getCredentials();
      const data = (response as any)?.data || response;
      credentials.value = Array.isArray(data) ? data : (data?.credentials || []);

      // Auto-select first matching credential
      const matchingCreds = credentials.value.filter(isMatchingCredential);
      if (matchingCreds.length > 0) {
        selectedCredentialId.value = matchingCreds[0].id;
      } else {
        selectedCredentialId.value = null;
      }
    } catch (error) {
      console.error('[useCredentialManagement] Failed to load credentials:', error);
      credentials.value = [];
    } finally {
      isLoadingCredentials.value = false;
    }
  };

  const selectCredential = (credential: Credential) => {
    selectedCredentialId.value = credential.id;
  };

  const openManageCredential = () => {
    showManageCredentialDialog.value = true;
  };

  const handleCredentialAdded = async (credential: Credential) => {
    await loadCredentials();
    selectedCredentialId.value = credential.id;
  };

  return {
    credentials,
    isLoadingCredentials,
    selectedCredentialId,
    selectedCredential,
    showManageCredentialDialog,
    mycoidCredentials,
    credentialTypeLabel,
    loadCredentials,
    selectCredential,
    openManageCredential,
    handleCredentialAdded,
  };
}
